import React from 'react'

import { SxProps } from '@mui/material'

import { useAttributeOptions } from 'redux/hooks/AttributeOptions'

import { TMetricQueryParams } from 'types/GlobalMetric'
import { PeriodFilter } from 'types/GlobalWidget'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'

import { getParsedDateSpan } from 'utils/dateParser'
import { parseLabel } from 'utils/widgetData'

interface Props {
  queryParams: TMetricQueryParams
  setQueryParams: (params: TMetricQueryParams) => void
  sx?: SxProps
}

const MetricActiveFilters = (props: Props) => {
  const { queryParams, setQueryParams, sx } = props

  const { lang, t, translateEnum } = useTranslation()
  const attributeOptions = useAttributeOptions()

  const segmentByName = queryParams.segment_by
    ? attributeOptions[queryParams.segment_by]?.name
    : null

  const getFilterChipLabel = (key: string, value: { eq: string[] }) => {
    const option = Object.values(attributeOptions).find(
      (opt) => opt.attribute_id === key
    )

    const values = value.eq
      .map((v) =>
        option
          ? parseLabel(
            {
              label: v,
              technical_name: option.technical_name,
              type: option.type
            },
            lang
          )
          : v
      )
      .join(', ')

    return `${option?.name ?? key}: ${values}`
  }

  const handleDeleteFilter = (key: string) => {
    const remaining = Object.fromEntries(
      Object.entries(queryParams.filter ?? {}).filter(([k]) => k !== key)
    )

    setQueryParams({
      ...queryParams,
      filter: Object.keys(remaining).length > 0 ? remaining : undefined
    })
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 0.5,
        overflowX: 'auto',
        width: '100%',
        '&::-webkit-scrollbar': { height: 5 },
        '&::-webkit-scrollbar-track': {
          boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
          webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'action.disabled',
          borderRadius: 2.5,
          outline: 'none'
        },
        ...sx
      }}
    >
      {segmentByName ? (
        <Text color="text.secondary" noWrap={true} variant="caption">
          {t('widgets', 'breakdown_title')}:{' '}
          <Text component="span" fontWeight="medium" variant="caption">
            {segmentByName}
          </Text>
        </Text>
      ) : null}
      {queryParams.from_date || queryParams.to_date ? (
        <Chip
          color="primary"
          label={getParsedDateSpan(queryParams.from_date, queryParams.to_date)}
          onDelete={() =>
            setQueryParams({
              ...queryParams,
              from_date: undefined,
              to_date: undefined
            })
          }
          size="small"
        />
      ) : null}
      {queryParams.period_filter &&
      queryParams.period_filter !== PeriodFilter.LAST_12_MONTHS_TO_TODAY ? (
            <Chip
              color="primary"
              label={
                translateEnum('period_filters', queryParams.period_filter).title
              }
              onDelete={() =>
                setQueryParams({ ...queryParams, period_filter: undefined })
              }
              size="small"
            />
          ) : null}
      {queryParams.filter
        ? Object.entries(queryParams.filter).map(([key, value]) => (
          <Chip
            color="primary"
            key={key}
            label={getFilterChipLabel(key, value)}
            onDelete={() => handleDeleteFilter(key)}
            size="small"
          />
        ))
        : null}
      <Chip
        clickable={true}
        label={t('global', 'clear_all')}
        onClick={() => setQueryParams({})}
        size="small"
      />
    </Box>
  )
}

export default MetricActiveFilters
